import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { useSpendingBucketsApi } from '../api/spending-buckets-api'
import {
  SpendingBucketsTransaction,
  SpendingBucketsTransactionsResponse,
} from '../types'
import { useSpendingBucketsView } from './SpendingBucketsView'
import { spendingBucketsServerErrors } from '../utils/tagging'
import { API_STATUS } from '../utils/constants'
import { getServerTimeShortDate, getServerTime } from '../utils/formatter'

// Data for each bucket saved on internal state
interface IBucketState {
  toolkitBucketId: string
  bucketBalance: number
  bucketCategoryName?: string
  totalPages: number
  totalRecords: number
  bucketBalanceLastUpdateDate?: string
  shouldRefetch: boolean
  byPage: {
    [key: number]: SpendingBucketsTransaction[]
  }
}

// Internal context state, not directly available in consumers
interface IBucketTransactionsState {
  page: number
  status: { [key: string]: API_STATUS }
  data: {
    [key: string]: IBucketState
  }
}

// Context value, available to consumers
export interface BucketTransactionsContextProps {
  selectedBucketData: IBucketState
  isPending: boolean
  hasError: boolean
  page: number
  nextPage: () => void
  prevPage: () => void
  invalidateTransactionCache: (toolkitBucketId: string) => void
}

// Actions for the transaction reducer
type ITransactionReducerAction =
  | {
      type: 'SET_STATUS'
      status: API_STATUS
      toolkitBucketId: string
    }
  | {
      type: 'UPDATE_BUCKET_TRANSACTIONS'
      response: SpendingBucketsTransactionsResponse
      page: number
    }
  | {
      type: 'INVALIDATE_CACHE'
      toolkitBucketId: string
      resetPage: boolean
    }
  | {
      type: 'SET_PAGE'
      page: number
    }

export const SpendingBucketsTransactionsContext = createContext(
  {} as BucketTransactionsContextProps,
)

const cleanseTransactions = (
  transactions: SpendingBucketsTransaction[],
  isPending = false,
): SpendingBucketsTransaction[] => {
  if (!transactions || transactions.length === 0) return []

  /* Pending transactions */
  if (isPending) {
    return transactions.map(transaction => ({
      ...transaction,
      transactionDate: 'Pending',
      transactionPostingTime: 'Pending',
    }))
  }
  /* Posted transactions */
  return transactions.map(transaction => ({
    ...transaction,
    transactionDate: transaction.transactionDate
      ? getServerTimeShortDate(transaction.transactionDate)
      : '',
    transactionPostingTime: transaction.transactionPostingTime
      ? getServerTime(transaction.transactionPostingTime)
      : '',
  }))
}

export const transactionsReducer = (
  state: IBucketTransactionsState,
  action: ITransactionReducerAction,
): IBucketTransactionsState => {
  switch (action.type) {
    case 'SET_STATUS': {
      return {
        ...state,
        status: { ...state.status, [action.toolkitBucketId]: action.status },
      }
    }
    case 'UPDATE_BUCKET_TRANSACTIONS': {
      const { response, page } = action
      const {
        postedTransactions,
        pendingTransactions,
        totalRecords,
        ...bucket
      } = response

      const transactions =
        page === 1
          ? [
              ...cleanseTransactions(pendingTransactions, true),
              ...cleanseTransactions(postedTransactions),
            ]
          : cleanseTransactions(postedTransactions)

      return {
        ...state,
        data: {
          ...state.data,
          [bucket.toolkitBucketId]: {
            ...bucket,
            totalRecords: totalRecords + pendingTransactions.length,
            shouldRefetch: false,
            byPage: {
              ...(state.data[bucket.toolkitBucketId]?.byPage || {}),
              [page]: transactions,
            },
          },
        },
      }
    }
    case 'INVALIDATE_CACHE': {
      const { toolkitBucketId, resetPage } = action
      return {
        ...state,
        page: resetPage ? 1 : state.page,
        data: {
          ...state.data,
          [toolkitBucketId]: {
            ...state.data[toolkitBucketId],
            byPage: {},
            shouldRefetch: resetPage,
          },
        },
      }
    }
    case 'SET_PAGE': {
      const { page } = action
      return {
        ...state,
        page,
      }
    }
    default:
      return state
  }
}

export const SpendingBucketsTransactionsProvider: React.FC<{
  initialValues?: IBucketTransactionsState
}> = ({ children, /* istanbul ignore next */ initialValues = {} }) => {
  const { getSpendingBucketsTransactions } = useSpendingBucketsApi()
  const {
    state: { selectedBucket },
  } = useSpendingBucketsView()

  const [state, dispatch] = useReducer(transactionsReducer, {
    page: 1,
    status: {},
    data: {},
    ...initialValues,
  })

  // Computed properties for ease of use
  const selectedBucketId = selectedBucket?.toolkitBucketId || ''
  const isPending = state.status[selectedBucketId] === API_STATUS.PENDING
  const hasError = state.status[selectedBucketId] === API_STATUS.REJECTED
  const shouldRefetch = state.data[selectedBucketId]?.shouldRefetch
  const selectedBucketData = state.data[selectedBucketId] || {}

  /**
   * Fetch transactions for the currently selected bucket/page.
   *  Ignores if the fetch is already pending or data is in cache
   */
  const getTransactions = async (): Promise<void> => {
    if (
      !selectedBucketId ||
      isPending ||
      selectedBucketData.byPage?.[state.page]
    ) {
      return
    }

    try {
      dispatch({
        type: 'SET_STATUS',
        status: API_STATUS.PENDING,
        toolkitBucketId: selectedBucketId,
      })
      const response = await getSpendingBucketsTransactions({
        bucketId: selectedBucketId,
        pageNumber: state.page,
      })
      dispatch({
        type: 'UPDATE_BUCKET_TRANSACTIONS',
        response,
        page: state.page,
      })
      dispatch({
        type: 'SET_STATUS',
        status: API_STATUS.RESOLVED,
        toolkitBucketId: selectedBucketId,
      })
    } catch (error) {
      dispatch({
        type: 'SET_STATUS',
        status: API_STATUS.REJECTED,
        toolkitBucketId: selectedBucketId,
      })
      spendingBucketsServerErrors.getTransactionsFailure(error as Error)
    }
  }

  /* Page updaters, will kick off a transactions fetch */
  const nextPage = (): void =>
    dispatch({ type: 'SET_PAGE', page: state.page + 1 })
  const prevPage = (): void =>
    dispatch({ type: 'SET_PAGE', page: state.page - 1 })

  /**
   * Delete transactions for a given bucket. If the user is
   * currently on the bucket that we're invalidating, set page
   * to 1 and re-fetch immediately by setting `shouldFetch` to true
   */
  const invalidateTransactionCache = (toolkitBucketId: string): void => {
    if (toolkitBucketId && state.data[toolkitBucketId]) {
      dispatch({
        type: 'INVALIDATE_CACHE',
        toolkitBucketId,
        resetPage: toolkitBucketId === selectedBucketId,
      })
    }
  }

  /* Watch page for changes and fetch latest transactions */
  useEffect(() => {
    getTransactions()
  }, [state.page])

  /* Watch selected bucket for changes. If we're already on
  page 1 call getTransactions directly, otherwise fire off a page
  update which will get picked up in the above useEffect */
  useEffect(() => {
    if (state.page === 1) {
      getTransactions()
    } else {
      dispatch({ type: 'SET_PAGE', page: 1 })
    }
  }, [selectedBucketId])

  /* Re-fetch transactions for the current bucket
  after they've been invalidated */
  useEffect(() => {
    if (shouldRefetch) {
      getTransactions()
    }
  }, [shouldRefetch])

  return (
    <SpendingBucketsTransactionsContext.Provider
      value={{
        /* State data */
        selectedBucketData,
        page: state.page,

        /* Computed pending/error properties */
        isPending,
        hasError,

        /* Updaters */
        nextPage,
        prevPage,
        invalidateTransactionCache,
      }}
    >
      {children}
    </SpendingBucketsTransactionsContext.Provider>
  )
}

export const useSpendingBucketsTransactions =
  (): BucketTransactionsContextProps => {
    return useContext(SpendingBucketsTransactionsContext)
  }
